import { Controller } from "@hotwired/stimulus"

// Phase 2.5 Tier 3 — Capital Request admin queue. Powers the bulk-action
// toolbar (mark for scoring / reject / withdraw), the per-row checkbox
// state, form-filter auto-submit, and the detail modal (Fund Sources-
// style — row click and "New request" both open a Turbo Frame modal
// instead of full-page navigating). The same `capital_request_modal`
// frame hosts the show, new, and edit pages so create/update redirects
// land back in the modal automatically.
export default class extends Controller {
  static targets = [
    "bulkBar", "selectedCount", "bulkActionKind", "bulkIdsContainer",
    "detailModal", "intakeLinksModal"
  ]

  connect() {
    this._selectedIds = new Set()
  }

  // ── Detail modal (row click / New / Edit → Turbo Frame fetch) ─────
  showDetailModal() {
    this._show(this.detailModalTarget)
  }

  hideDetailModal() {
    const modal = document.querySelector('[data-capital-request-admin-target="detailModal"]')
    if (!modal) return
    modal.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    const frame = modal.querySelector("turbo-frame#capital_request_modal")
    if (frame) {
      frame.innerHTML = `<div class="text-center text-sm text-gray-400 py-8">Loading…</div>`
      // Clear src so re-opening fetches a fresh page (otherwise Turbo
      // sees the frame as already-loaded and skips the request).
      frame.removeAttribute("src")
      frame.removeAttribute("complete")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Public intake links modal ─────────────────────────────────────
  showIntakeLinksModal() {
    this._show(this.intakeLinksModalTarget)
  }

  hideIntakeLinksModal() {
    if (!this.hasIntakeLinksModalTarget) return
    this.intakeLinksModalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  async copyIntakeLink(event) {
    const btn = event.currentTarget
    const url = btn.dataset.intakeUrl
    if (!url) return

    let copied = false
    try {
      await navigator.clipboard.writeText(url)
      copied = true
    } catch (_e) {
      // Fall back to selecting the adjacent input + execCommand. Works
      // even outside secure contexts; no browser-native prompt.
      const input = btn.previousElementSibling
      if (input && input.tagName === "INPUT") {
        input.focus()
        input.select()
        try { copied = document.execCommand("copy") } catch (_e2) { copied = false }
      }
    }

    const original = btn.textContent
    btn.textContent = copied ? "Copied!" : "Press Cmd+C"
    setTimeout(() => { btn.textContent = original }, 1500)
  }

  _show(modal) {
    if (!modal) return
    modal.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  // Auto-submit the filter form when a select changes.
  submitFilter(event) {
    event?.target?.form?.requestSubmit?.()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.element.querySelectorAll('input[type="checkbox"][data-capital-request-id]')
      .forEach(cb => {
        cb.checked = checked
        const id = cb.dataset.capitalRequestId
        if (checked) {
          this._selectedIds.add(id)
        } else {
          this._selectedIds.delete(id)
        }
      })
    this._refreshBulkBar()
  }

  toggleRow(event) {
    const id = event.target.dataset.capitalRequestId
    if (event.target.checked) {
      this._selectedIds.add(id)
    } else {
      this._selectedIds.delete(id)
    }
    this._refreshBulkBar()
  }

  submitBulkMarkScoring() { this._submitBulk("mark_for_scoring") }

  submitBulkReject() {
    const reason = window.prompt("Reason for bulk rejection (required):")
    if (!reason || reason.trim() === "") return
    this._submitBulk("reject", { reason: reason.trim() })
  }

  submitBulkWithdraw() {
    if (!window.confirm("Withdraw the selected requests? This is permanent.")) return
    this._submitBulk("withdraw")
  }

  _submitBulk(action, extras = {}) {
    if (this._selectedIds.size === 0) {
      window.alert("Select at least one request first.")
      return
    }
    if (!this.hasBulkActionKindTarget || !this.hasBulkIdsContainerTarget) return

    this.bulkActionKindTarget.value = action
    this.bulkIdsContainerTarget.innerHTML = ""

    this._selectedIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "capital_request_ids[]"
      input.value = id
      this.bulkIdsContainerTarget.appendChild(input)
    })

    Object.entries(extras).forEach(([key, value]) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = key
      input.value = value
      this.bulkIdsContainerTarget.appendChild(input)
    })

    this.bulkActionKindTarget.form.requestSubmit()
  }

  _refreshBulkBar() {
    if (!this.hasBulkBarTarget) return
    const count = this._selectedIds.size
    if (count > 0) {
      this.bulkBarTarget.classList.remove("hidden")
    } else {
      this.bulkBarTarget.classList.add("hidden")
    }
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = String(count)
    }
  }
}
